import { useQuery } from '@tanstack/react-query';
import { useAtomValue } from 'jotai';
import { useMemo } from 'react';

import { formatWorkHistoryItemContent } from '@/components/profile/WorkHistorySection';
import { getCancerTypesV2QueryOptions } from '@/services/getCancerTypesV2';
import { getResponseTimeSettingsV2QueryOptions } from '@/services/getResponseTimeSettingsV2';
import { getUserPublicV2, getUserPublicV2QueryOptions } from '@/services/getUserPublicV2';
import type { UserPublicV2 } from '@/services/getUserPublicV2';
import { userIdAtom } from '@/services/globalState';
import { getProfileNameAndInitials } from '@/util/getProfileNameAndInitials';
import { mapToBioSections } from '@/util/mapBioSections';
import { isAskAnonymously } from '@/util/userFilters';
import { sortByYear } from '@/utils/profileHelpers';

type UserData = UserPublicV2 | undefined;

type WorkHistoryItem = NonNullable<UserPublicV2['workHistory']>[number];

type Mode = 'auto' | 'public-only';

/**
 * Single source of truth for expert profile data + derivations.
 *
 * Always reads the public endpoint, for your own profile as much as anyone
 * else's. A profile is public information, and the private user record exists
 * for account settings rather than for rendering one: the security fix that
 * separated the two said cross-user lookups belong on the public endpoint, and
 * a profile page is that lookup even when it happens to point at you.
 *
 * It used to switch to the private endpoint for your own profile, which is how
 * an expert came to be unable to see their own publications while every other
 * viewer could. Only the private payload omitted the section counts, so the
 * sections silently never mounted. Reading one endpoint means the two cannot
 * disagree again.
 *
 * `mode` no longer selects a data source. It only decides whether ownership is
 * claimed at all, which is what draws the edit affordances: `public-only`
 * suppresses them for the slim profile and the directory sidebar.
 *
 * Expects a numeric id. The self-referring URL is canonicalised by the route, so
 * an alias never reaches here.
 */
export function useExpertProfileData(
  id: string | number | undefined,
  options: { mode?: Mode; retryFailedOnMount?: boolean } = {}
) {
  const mode = options.mode ?? 'auto';

  const currentUserId = useAtomValue(userIdAtom);

  const isLoggedIn = !!currentUserId;

  const idString = id?.toString();

  const isOwnProfile =
    mode === 'auto' && isLoggedIn && !!idString && idString === currentUserId?.toString();

  /**
   * The id is always a number here: the self-referring URL is resolved to one by
   * the route before this renders, so there is no alias left to interpret.
   */
  const hasNumericId = !!idString && !Number.isNaN(Number(idString));

  const publicQuery = useQuery({
    ...getUserPublicV2QueryOptions(idString, { retryFailedOnMount: options.retryFailedOnMount }),
    enabled: hasNumericId,
    queryFn: () => getUserPublicV2(Number(idString)),
  });

  const userData: UserData = publicQuery.data;

  const userLoading = publicQuery.isLoading;

  const cancerTypesQuery = useQuery(getCancerTypesV2QueryOptions());

  const responseTimeQuery = useQuery(getResponseTimeSettingsV2QueryOptions());

  const responseTimeBuckets = responseTimeQuery.data ?? [];

  const cancerFocusAreas = useMemo(
    () =>
      userData?.cancerFocusIds
        ?.map((cancerFocus) => {
          const entry = cancerTypesQuery.data?.cancerTypeLookup.get(cancerFocus.id);

          return entry?.shortLabel || entry?.label || '';
        })
        .filter(Boolean)
        .join(', ') || '',
    [userData, cancerTypesQuery.data]
  );

  const subspecialtyItems = useMemo(() => {
    const list = (userData?.subspecialties ?? []) as { label?: string | null }[];

    return list.map((entry) => entry?.label || '').filter(Boolean);
  }, [userData]);

  const responseTimeLabel = useMemo(() => {
    if (!userData?.responseTime) {
      return '';
    }

    const bucket = responseTimeBuckets.find(
      (entry) => Number(userData.responseTime) <= entry.maxTime
    );

    return bucket?.label.replace(' time', '') || '';
  }, [userData?.responseTime, responseTimeBuckets]);

  const userType = (userData?.userType || 'lmp') as 'lmp' | 'expert';

  const bioSections = useMemo(() => {
    const workHistoryItems = (userData?.workHistory || []).map((item) => {
      const structuredContent = formatWorkHistoryItemContent(item as WorkHistoryItem);

      return {
        ...item,
        accolade: structuredContent.map((part) => part.text).join('\n'),
        accoladeParts: structuredContent,
      };
    });

    const sortedWorkHistory = sortByYear(workHistoryItems);

    const disclosures = userType === 'expert' ? userData?.disclosures || [] : [];

    const sections = mapToBioSections({
      workHistory: sortedWorkHistory,
      education: sortByYear(userData?.education || []),
      disclosures,
      about: userData?.blurb || '',
    });

    const sectionsWithParts = sections.map((section) =>
      section.title === 'Work History'
        ? {
            ...section,
            data: section.data.map((item, index) => ({
              ...item,
              accoladeParts: sortedWorkHistory[index]?.accoladeParts,
            })),
          }
        : section
    );

    if (
      userType === 'expert' &&
      !sectionsWithParts.some((section) => section.title === 'Disclosures')
    ) {
      return [...sectionsWithParts, { title: 'Disclosures', data: [] }];
    }

    return sectionsWithParts;
  }, [userData, userType]);

  const { name, nameWithoutDoctor } = useMemo(
    () =>
      getProfileNameAndInitials({
        firstName: userData?.firstName || '',
        lastName: userData?.lastName || '',
        identifiesAsDoctor: userData?.identifiesAsDoctor,
      }),
    [userData]
  );

  return {
    userData,
    isLoading: userLoading || cancerTypesQuery.isLoading,
    isOwnProfile,
    isAnonymous: isAskAnonymously(userData?.lastName),
    name,
    nameWithoutDoctor,
    cancerFocusAreas,
    subspecialtyItems,
    responseTimeLabel,
    bioSections,
  };
}

export type ExpertProfileData = ReturnType<typeof useExpertProfileData>;
